import { Alert, Button, ModalV2 } from '@root/components'
import { Spinner } from '@root/components/ui/Primitives'
import { useBusinessContext } from '@root/context'
import { previewSoftReminderEmail } from '@root/query'
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FaFilePdf } from 'react-icons/fa'
import styled from 'styled-components'

interface Props {
  isVisible: boolean
  onClose: () => void
  title: string
  language: string
  message: string
}

export const ReminderEmailPreviewModal: React.FC<Props> = ({
  isVisible,
  onClose,
  title,
  language,
  message
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const previewQuery = useQuery({
    queryKey: ['soft-reminder-email-preview', slug, language, message],
    queryFn: () => previewSoftReminderEmail({ slug, language, message }),
    enabled: isVisible
  })

  return (
    <ModalV2
      header={title}
      isVisible={isVisible}
      handleOnClose={onClose}
      width={720}
      footer={
        <Footer>
          <Button type="button" onClick={onClose}>
            {t('general.close')}
          </Button>
        </Footer>
      }
    >
      {previewQuery.isPending && (
        <LoadingState>
          <Spinner />
        </LoadingState>
      )}
      {previewQuery.isError && <Alert type="error" description={t('general.error')} />}
      {previewQuery.data && (
        <PreviewStack>
          <SubjectRow>
            <SubjectLabel>{t('settings.invoicing.softReminderEmailSubjectLabel')}</SubjectLabel>
            <SubjectValue>{previewQuery.data.subject}</SubjectValue>
          </SubjectRow>
          <EmailFrame title={title} srcDoc={previewQuery.data.html} sandbox="" />
          <AttachmentCard aria-hidden>
            <FaFilePdf />
            <AttachmentCopy>
              <AttachmentName>{previewQuery.data.attachment_name}</AttachmentName>
              <AttachmentHint>{t('settings.invoicing.softReminderInvoiceAttached')}</AttachmentHint>
            </AttachmentCopy>
          </AttachmentCard>
        </PreviewStack>
      )}
    </ModalV2>
  )
}

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg}rem;
`

const PreviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-height: 0;
`

const SubjectRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const SubjectLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.iconGray};
`

const SubjectValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.45;
`

const EmailFrame = styled.iframe`
  width: 100%;
  min-height: 16rem;
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const AttachmentCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  pointer-events: none;

  svg {
    color: ${({ theme }) => theme.colors.error};
    font-size: ${({ theme }) => theme.fontSize.lg}rem;
    flex-shrink: 0;
  }
`

const AttachmentCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const AttachmentName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
`

const AttachmentHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`
